import React, { createContext, useContext } from 'react';
import { workProjectService } from '../services/ProjectService';

/**
 * Which projects collection the surrounding task UI is operating on.
 *
 * Mirrors TaskServiceContext exactly, including the property that makes it
 * boring: THE DEFAULT IS THE WORK SERVICE. A component rendered with no
 * provider around it resolves to precisely the binding it used to import at
 * module level, so /portal/work, the teacher dashboard and the admin home
 * behave identically without being touched. Only a subtree that deliberately
 * wraps itself gets different data.
 *
 * A MODULE-LEVEL IMPORT IS A HARDCODED COLLECTION — that is the lesson the task
 * mount already paid for. Parameterising the service was necessary and not
 * sufficient; WorkTasksModule had to stop importing `subscribeToProjects`
 * directly, or the Personal board would have rendered the studio's five
 * projects while writing to `personalProjects`.
 *
 * WHAT DOES NOT BELONG HERE: `computeProjectProgress`. It is pure — it takes
 * tasks and a projectId and does arithmetic — so it touches no collection and
 * stays a direct import. Routing it through the context would imply it varies
 * per space, which it does not. (Same call, same reasoning, as taskChangeState
 * on the task context.)
 *
 * The default is a module-level constant, so the context value is
 * referentially stable and effects depending on it never re-fire.
 */
const ProjectServiceContext = createContext(workProjectService);

/**
 * Bind a subtree to a specific space's project service.
 *
 * @param {object} props.service a service from createProjectService (e.g.
 *   personalProjectService). Omit to inherit the Work default.
 */
export function ProjectServiceProvider({ service, children }) {
	return (
		<ProjectServiceContext.Provider value={service || workProjectService}>
			{children}
		</ProjectServiceContext.Provider>
	);
}

export function useProjectService() {
	return useContext(ProjectServiceContext);
}

export default ProjectServiceContext;
